import {booleanAttribute, ChangeDetectionStrategy, Component, computed, ElementRef, Inject, InjectionToken, input, output, Signal, signal, viewChild} from "@angular/core";
import {DokumentId, DokumentType, DokumentVersjonModell, SigneringStatus, TynnMøteModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {dokumentSkalIkkeSigneresAvBruker, Signatarinformasjon, signatarinformasjon} from "felles-ng2/modell/dokumentModellHjelpere";
import {DokumentvisningKomponentAvhengigheter} from "./DokumentvisningKomponentAvhengigheter";
import {outputAbonnenterRegistrert} from "felles-ng2/infrastruktur/angular/komponentOutput";
import {AnimationEvent} from "@angular/animations";
import {scrollTilSenter, scrollTilTopp} from "web-ng2/delt/dom/scrollIntoView";
import {DokumentInlinePdfviserKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-inline-pdfviser/dokument-inline-pdfviser";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {DokumentInformasjonTjeneste} from "felles-ng2/tjenester/DokumentInformasjonTjeneste";
import {ÅpneDokumentTjeneste} from "web-ng2/moduler/dokument/tjenester/ÅpneDokumentTjeneste";
import {DokumentSigneringTjeneste} from "web-ng2/moduler/dokument/tjenester/DokumentSigneringTjeneste";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {distinctUntilChanged, filter, firstValueFrom, Observable, of, shareReplay, switchMap} from "rxjs";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {distinctEntitetIdUntilChanged} from "felles-ng2/støttefunksjoner/observable";

/**  InjectionToken for injisering av {@link DelegerteDokumentvisningOperasjoner} implementasjon. */
export const DelegerteDokumentvisningOperasjoner_DI_TOKEN = new InjectionToken<DelegerteDokumentvisningOperasjoner>('DelegerteDokumentvisningOperasjoner implementasjon');

@Component({
    selector: 'abstrakt-dokumentvisning',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export abstract class DokumentvisningBasisKomponent {

    /** css selector uttrykk som kan benyttes for å lokalisere elmenet som kan benyttes i 'scroll til' uttrykk. */
    static scrollTilElementCssUttrykk(dokumentId: DokumentId): string {
        return `#dok-${dokumentId}`;
    }

    readonly dokumentHoeydeEkspandert = '706px';
    readonly dokumentTyper = DokumentType;

    protected inlineViser = viewChild(DokumentInlinePdfviserKomponent, {read: ElementRef});

    vedAapneInline = output();

    dokumentversjon = input.required<DokumentVersjonModell>();
    /** Dersom true vil ikke signering-knappen vises i dokumentvisningen, selvom bruker er satt til å signere */
    signeringDeaktivert = input(false, {transform: booleanAttribute});
    /** Dersom true vises ikke ikon som markerer at dokumentet er skjermet, dersom dette er tilfellet.
     * MERK at det ikke skjuler relaterte menyinnslag - disse må administreres via {@link DelegerteDokumentMenyOperasjoner */
    visSkjermingDeaktivert = input(false, {transform: booleanAttribute});
    /** Dersom true vil ikke 'ny' banneret vises i dokumentvisningen, selvom bruker ikke har lest dokumentet */
    visNymerkingDeaktivert = input(false, {transform: booleanAttribute});
    /** Dersom sann og dokumentet egner seg for inline visning vises knapp for inline-vising */
    visAapneInline = input(true, {transform: booleanAttribute});

    informasjon = computed(() => this.dokumentInformasjon.dokumentinformasjon(this.dokumentversjon()));
    signatarinformasjonSomObserverbar = this.lastSignatarinformasjon();
    signatarinformasjon = toSignal(this.signatarinformasjonSomObserverbar, {initialValue: dokumentSkalIkkeSigneresAvBruker()});
    viserInline = signal(false);
    stoetterInlinevisning = computed(() => this.visAapneInline() && this.informasjon().inlineVisningMulig);
    erFavorittmarkert = computed(() => this.avhengigheter.erFavoritt(this.dokumentversjon()));
    /** Sann dersom elektronisk signatur er aktivert for dokumentet og signeringDeaktivert ikke er sann.
     * Merk at for at protokolldokumenter sendt til signering skal få elektroniskSigneringAktivert=true sjekker vi også mot {@link #signatarinformasjon} */
    elektroniskSigneringAktivert = computed(() => !this.signeringDeaktivert() && (this.dokumentversjon().signeringStatus != SigneringStatus.ikkeAktivert || this.signatarinformasjon().skalSignere));
    elektroniskSigneringHjelpetekst = computed(() => this.beregnElektroniskSigneringHjelpetekst());
    moeteProtokolldokumentISakErProtokollFor = toSignal(this.lastMoeteProtokolldokumentISakErProtokollFor(), {initialValue: null});

    private signeringPågår = false;

    constructor(protected avhengigheter: DokumentvisningKomponentAvhengigheter,
                @Inject(DelegerteDokumentvisningOperasjoner_DI_TOKEN) protected delegerteOperasjoner: DelegerteDokumentvisningOperasjoner,
                protected dokumentInformasjon: DokumentInformasjonTjeneste,
                protected dokumentTjeneste: ÅpneDokumentTjeneste,
                protected dokumentSigneringTjeneste: DokumentSigneringTjeneste,
                protected oversettelser: Oversettelser,
                protected navigasjon: Navigasjon) {
    }

    /** Scroller slik at dokumentet havner i senter av nærmeste scrollområde (f.eks. .saksdetaljer-scrollomraade i sak-liste-og-detaljer) */
    scrollTil() {
        scrollTilSenter(DokumentvisningBasisKomponent.scrollTilElementCssUttrykk(this.dokumentversjon().dokumentId));
    }

    visSkjerming() {
        this.delegerteOperasjoner.visSkjerming(this.dokumentversjon());
    }

    visSignatarer() {
        this.delegerteOperasjoner.visSignatarer(this.dokumentversjon());
    }

    aapneStandardformat() {
        //dokumentTjeneste.åpne vil sørge for å oppdaterer lokallager med info om at dokument nå er lest
        this.dokumentTjeneste.åpne(this.dokumentversjon(), this.informasjon().standardformat);
    }

    aapneInline() {
        if (!this.stoetterInlinevisning()) return;
        if (outputAbonnenterRegistrert(this.vedAapneInline))
            return this.vedAapneInline.emit();
        this.viserInline.set(true);

        if (!this.dokumentversjon().erLestAv) this.dokumentTjeneste.markerDokumentSomLest(this.dokumentversjon());
    }

    inlineVisningFerdigAnimert(hendelse: AnimationEvent) {
        if (this.erVisInlineAnimasjonFerdig(hendelse))
            scrollTilTopp(this.inlineViser()!.nativeElement, 100); //uten forsinkelse fungerer ikke scroll i dokument-kortvisning
    }

    lukkInlineVising() {
        this.viserInline.set(false);
    }

    /** Viser modal for signering av dokument.
     Dersom signering allerede pågår skjer ingen ting. */
    async signer() {
        if (this.signeringDeaktivert() || this.signeringPågår) return;

        //signering kan ikke initieres for protokolldokumentISak - må gjøres fra protokoll
        if (this.dokumentversjon().type == DokumentType.protokolldokumentISak) {
            this.gaaTilProtokoll();
            return;
        }

        this.signeringPågår = true;
        //merk at vi her bruker signaturinformasjonSomObserverbar og ikke den signal baserte signaturinformasjon da vi sikre at informasjonen er lastet fra server
        const signatarinformasjon = await firstValueFrom(this.signatarinformasjonSomObserverbar);
        await this.dokumentSigneringTjeneste.signer(this.dokumentversjon(), signatarinformasjon);
        this.signeringPågår = false;
    }

    gaaTilProtokoll(){
        this.navigasjon.gåTilMøteProtokollFraProtokolldokumentISak(this.dokumentversjon().dokumentId);
    }

    /** Sann dersom angitt hendelse indikerer at animasjon av visning av inline viser er ferdig */
    protected abstract erVisInlineAnimasjonFerdig(hendelse: AnimationEvent): boolean;

    //private hjelpemetoder

    /** Laster signaturinformasjon dersom dokumentet er markert for signering og signering ikke er eksplisitt deaktivert.
     * Sørger for at signatarinformasjon kun lastes én gang selv om det opprettes multiple subscriptions. */
    private lastSignatarinformasjon(): Observable<Signatarinformasjon> {
        return toObservable(this.dokumentversjon).pipe(
          //last signatarinformasjon kun dersom dokument er endret eller signeringstatus er endret
          distinctUntilChanged((a, b) => a.id === b.id && a.signeringStatus === b.signeringStatus),
          switchMap(dokumentversjon => this.signatarinformasjonDersomSigneringIkkeDeaktivert(dokumentversjon)),
          //sørger for at ikke hver subscription fører til kall til avhengigheter.signatarinformasjonForAktivBruker(..),
          //`refCount: true` slik at kilde unsubscribes når alle subscriptions har terminert
          shareReplay({bufferSize: 1, refCount: true})
        );
    }

    private signatarinformasjonDersomSigneringIkkeDeaktivert(dokumentversjon: DokumentVersjonModell): Observable<Signatarinformasjon> {
        if (this.signeringDeaktivert()) return of(dokumentSkalIkkeSigneresAvBruker());
        return signatarinformasjon(dokumentversjon, id => this.avhengigheter.signatarinformasjonForAktivBruker(id))
    }

    private beregnElektroniskSigneringHjelpetekst(): string {
        switch (this.dokumentversjon().signeringStatus) {
            case SigneringStatus.tilSignering:
                return this.oversettelser.oversett(i18n('Dokumentet er sendt til signering, klikk for å se detaljer'));
            case SigneringStatus.delvisSignert:
                return this.oversettelser.oversett(i18n('Dokumentet er sendt til signering, men ikke alle har signert, klikk for å se detaljer'));
            case SigneringStatus.signert:
                return this.oversettelser.oversett(i18n('Dokumentet er signert av alle, klikk for å se detaljer'));
            case SigneringStatus.signertOgKonvoluttert:
                return this.oversettelser.oversett(i18n('Dokumentet er ferdig signert, klikk for å se detaljer'));
            case SigneringStatus.eksterntSignert:
                return this.oversettelser.oversett(i18n('Dokumentet er signert utenfor Styreplan, klikk for å se detaljer'));
            default:
                return '';
        }
    }

    private lastMoeteProtokolldokumentISakErProtokollFor(): Observable<TynnMøteModell | null> {
        return toObservable(this.dokumentversjon).pipe(
          distinctEntitetIdUntilChanged(),
          filter(() => this.dokumentversjon().type == DokumentType.protokolldokumentISak),
          switchMap(protokolldokumentISak => this.avhengigheter.finnMøteProtokolldokumentISakErProtokollFor(protokolldokumentISak.dokumentId)),
        );
    }

}

/** Operasjoner som DokumentvisningBasisKomponent ikke selv kan realisere, men som noen høyere opp i komponent-treet må realisere */
export interface DelegerteDokumentvisningOperasjoner {
    /** Viser skjermingsstatus for angitt dokument, gitt at {@link DokumentvisningBasisKomponent#visSkjermingDeaktivert} ikke er satt til true.*/
    visSkjerming(dokumentversjon: DokumentVersjonModell): void;
    /** Viser signeringsinformasjon (signatarer) for angitt dokument, gitt at {@link DokumentvisningBasisKomponent#signeringDeaktivert} ikke er satt til true.*/
    visSignatarer(dokumentversjon: DokumentVersjonModell): void;
}
